
import { GA_MEASUREMENT_ID } from './constants';

/**
 * Core analytics functionality including initialization and basic tracking
 */
export class AnalyticsCore {
  protected initialized: boolean = false;
  protected userId: string | null = null;
  // Change from private to protected so subclasses can access it
  protected sessionStartTime: number | null = null;

  constructor() {
    // Initialize session time
    this.sessionStartTime = Date.now();
    
    // Try to load analytics script only in browser environment
    if (typeof window !== 'undefined') {
      this.initGoogleAnalytics();
    }
  }

  /**
   * Initialize Google Analytics
   */
  protected initGoogleAnalytics() {
    if (this.initialized) return;
    
    try {
      // This checks if GA is already loaded
      if (window.gtag) {
        console.log('Google Analytics already initialized');
        this.initialized = true;
        return;
      }

      console.log('Initializing Google Analytics...');
      
      // Create script element to load gtag.js
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      
      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };
      
      // Set default configuration
      window.gtag('js', new Date());
      window.gtag('config', GA_MEASUREMENT_ID, {
        send_page_view: true,
        cookie_flags: 'SameSite=None;Secure'
      });
      
      // Add script to document
      document.head.appendChild(script);
      this.initialized = true;
      
      // Set up page tracking
      this.setupPageTracking();
      
    } catch (error) {
      console.error('Error initializing Google Analytics:', error);
    }
  }

  /**
   * Set user ID for tracking
   */
  setUserId(userId: string | null) {
    this.userId = userId;
    
    if (userId && window.gtag) {
      window.gtag('set', { 'user_id': userId });
      console.log(`Analytics: Set user_id to ${userId}`);
      
      // Track login event when user ID is set
      this.trackEvent({
        category: 'User',
        action: 'user_identified',
        label: 'Supabase Auth',
        nonInteraction: false
      });
    }
  }
  
  /**
   * Track page views automatically
   */
  protected setupPageTracking() {
    if (typeof window !== 'undefined') {
      // Track initial pageview
      this.trackPageView(window.location.pathname + window.location.search);
      
      // Listen for route changes if using browser history
      window.addEventListener('popstate', () => {
        this.trackPageView(window.location.pathname + window.location.search);
      });
      
      // For React Router, we can add a custom listener in the main App component
      // This is done separately in the App.tsx
    }
  }

  /**
   * Track a page view
   */
  trackPageView(path: string, title?: string) {
    if (!this.initialized || !window.gtag) return;
    
    try {
      // Include user_id if available
      const params: Record<string, any> = {
        page_path: path,
        page_title: title || document.title
      };
      
      // Add user_id if available
      if (this.userId) {
        params.user_id = this.userId;
      }
      
      window.gtag('event', 'page_view', params);
      console.log(`Analytics: Tracked page view for ${path}`);
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  }

  /**
   * Track a generic event
   */
  trackEvent(event: import('./types').AnalyticsEvent) {
    if (!this.initialized || !window.gtag) return;
    
    try {
      const eventParams: Record<string, any> = {
        event_category: event.category,
        event_label: event.label,
        value: event.value,
        non_interaction: event.nonInteraction || false,
        ...event.dimensions
      };
      
      // Add user_id if available
      if (this.userId) {
        eventParams.user_id = this.userId;
      }
      
      window.gtag('event', event.action, eventParams);
      console.log(`Analytics: Tracked event ${event.category}/${event.action}`, eventParams);
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }
}
